/**
 * 8D Module API - Comments
 *
 * Database operations for 8D communication log.
 * Uses mod_8d schema for module isolation.
 */

import { supabase } from '@/integrations/supabase/client';

// =============================================================================
// Types
// =============================================================================

export interface Comment {
  id: string;
  tenant_id: string;
  claim_id: string;
  step_number: number | null;
  author_type: string;
  author_id: string | null;
  author_name: string | null;
  content: string;
  created_at: string;
}

export interface CreateCommentInput {
  tenant_id: string;
  claim_id: string;
  step_number?: number | null;
  author_type: 'factory' | 'supplier';
  author_id?: string | null;
  author_name?: string | null;
  content: string;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Gets the tenant_id for a claim.
 */
async function getClaimTenantId(claimId: string): Promise<string | null> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('claims')
    .select('tenant_id')
    .eq('id', claimId)
    .single();

  if (error) return null;
  return data?.tenant_id ?? null;
}

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetches all comments for a claim in chronological order.
 */
export async function fetchCommentsByClaim(claimId: string): Promise<Comment[]> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('comments')
    .select('*')
    .eq('claim_id', claimId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data;
}

/**
 * Creates a comment from the factory side.
 */
export async function createFactoryComment(
  tenantId: string,
  claimId: string,
  authorId: string,
  content: string
): Promise<Comment> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('comments')
    .insert({
      tenant_id: tenantId,
      claim_id: claimId,
      author_type: 'factory',
      author_id: authorId,
      content,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Creates a factory comment with auto-fetched tenant_id.
 */
export async function createFactoryCommentForClaim(
  claimId: string,
  authorId: string,
  content: string
): Promise<Comment> {
  const tenantId = await getClaimTenantId(claimId);
  if (!tenantId) throw new Error('Claim not found');
  return createFactoryComment(tenantId, claimId, authorId, content);
}

/**
 * Creates a comment from the supplier side.
 */
export async function createSupplierComment(
  tenantId: string,
  claimId: string,
  authorName: string,
  content: string
): Promise<Comment> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('comments')
    .insert({
      tenant_id: tenantId,
      claim_id: claimId,
      author_type: 'supplier',
      author_name: authorName,
      content,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Creates an initial system comment when a claim is created.
 */
export async function createInitialComment(
  tenantId: string,
  claimId: string,
  authorId: string,
  problemDescription: string
): Promise<Comment> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('comments')
    .insert({
      tenant_id: tenantId,
      claim_id: claimId,
      author_type: 'factory',
      author_id: authorId,
      content: `New 8D claim created.\n\nProblem Description:\n${problemDescription}`,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}
